import {
  faChartDiagram,
  faPlus,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Breadcrumbs,
  Button,
  Grid,
  Icon,
  Input,
  Message,
  Modal,
} from "@intility/bifrost-react";
import Tabs from "@intility/bifrost-react/Tabs";
import { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";
import { IntilityIdException } from "../../../api/errorEnvelope";
import {
  useDeletePlatform,
  usePlatform,
  useRetryPlatformProvisioning,
} from "../../../api/platforms";
import type { ProvisioningGap } from "../../../api/types";
import { PlatformHeaderIllustration } from "../../../components/platforms/PlatformHeaderIllustration";
import { PlatformLogo } from "../../../components/platforms/PlatformLogo";
import { RoleBadge } from "../../../components/RoleBadge";
import { isFromMyHub } from "../../../lib/fromMyHub";
import { themeClass } from "../../../lib/platformIdentity";
import { BlueprintDetail } from "../../blueprints/BlueprintDetail";
import { PlatformAccess } from "./PlatformAccess";
import { PlatformClientsTab } from "./PlatformClientsTab";
import { PlatformOverview } from "./PlatformOverview";

type PlatformTab = "overview" | "clients" | "blueprint" | "access";

// The refusal keycloak-api returns while a company holds access-store tuples
// under the Platform (ADR-0059 decision 5). Read it from KcErrorCodes once the
// id-errors release that carries it (after 0.5.0) is picked up here.
const PLATFORM_HAS_LIVE_DATA = "kc.platform_has_live_data";

export default function PlatformDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: platform, isLoading, error } = usePlatform(id ?? "");
  const deletePlatform = useDeletePlatform();
  const retryProvisioning = useRetryPlatformProvisioning(id ?? "");
  const [confirmDelete, setConfirmDelete] = useState(false);
  // The owner types the slug before the delete is sent (ADR-0059 decision 7).
  const [typedSlug, setTypedSlug] = useState("");
  // ?tab=blueprint opens the Blueprint tab (old /blueprints/:slug links).
  // ?tab=clients&client=<id> opens the Clients tab with that client selected:
  // a client in a platform is viewed from the platform (see lib/clientPath).
  const [searchParams] = useSearchParams();
  const requestedClient = searchParams.get("client");
  const [tab, setTab] = useState<PlatformTab>(
    searchParams.get("tab") === "blueprint"
      ? "blueprint"
      : requestedClient || searchParams.get("tab") === "clients"
        ? "clients"
        : "overview",
  );
  // The page is reused when a client link leads to this same platform, so the
  // tab follows the link then too.
  useEffect(() => {
    if (requestedClient) setTab("clients");
  }, [requestedClient]);
  // Opened from My Hub: the breadcrumb leads back there, not to Platforms.
  const fromMyHub = isFromMyHub(location.state);
  // Warnings and ADR-0036 gaps come only from the create response, passed in
  // router state by RegisterPlatform — shown on this first landing, never re-fetched.
  const setupWarnings =
    (location.state as { setupWarnings?: string[] } | null)?.setupWarnings ??
    [];
  const [createGaps] = useState<ProvisioningGap[]>(
    (location.state as { gaps?: ProvisioningGap[] } | null)?.gaps ?? [],
  );
  // Gaps from a retry, shown until the user navigates away.
  const [retryGaps, setRetryGaps] = useState<ProvisioningGap[]>([]);

  if (isLoading) {
    return (
      <div className="bf-content bf-page-padding">
        <Grid gap={16} style={{ minHeight: "200px", placeContent: "center" }}>
          <Icon.Spinner size={32} />
        </Grid>
      </div>
    );
  }

  if (error || !platform) {
    return (
      <div className="bf-content bf-page-padding">
        <Message state="warning" header="Failed to load platform">
          {error instanceof Error ? error.message : "Platform not found."}
        </Message>
      </div>
    );
  }

  const provisioningStatus = platform.provisioningStatus ?? "active";
  // A Platform delete that did not finish (ADR-0059 decision 6). Every other
  // write is refused with 409 while it is set, so the edit actions go
  // inactive and the retry is the same DELETE.
  const deleting = provisioningStatus === "deleting";

  const canEdit =
    !deleting &&
    (platform.userRole === "owner" || platform.userRole === "maintainer");
  const canDelete = platform.userRole === "owner";

  // The text the owner types to confirm the delete. The slug is required at
  // creation; the name is the fallback for a Platform registered before that.
  const confirmText = platform.slug ?? platform.name;

  // The blueprint exists with the Platform (ADR-0059), so the builder opens
  // on this Platform's slug.
  function openBlueprintBuilder() {
    navigate(`/blueprints/builder/${platform?.slug}`);
  }

  async function handleDeletePlatform() {
    if (!id) return;
    try {
      await deletePlatform.mutateAsync(id);
      navigate("/platforms");
    } catch {
      // Error shown in the delete modal via deletePlatform.error
    }
  }

  // kc.platform_has_live_data is a refusal, not a failure: a company still
  // holds access-store tuples under the Platform (ADR-0059 decision 5).
  const deleteError = deletePlatform.error
    ? deletePlatform.error instanceof IntilityIdException &&
      deletePlatform.error.code === PLATFORM_HAS_LIVE_DATA
      ? "A company still holds access under this platform. Remove those grants first, then delete the platform."
      : deletePlatform.error instanceof Error
        ? deletePlatform.error.message
        : "Something went wrong."
    : null;

  async function handleRetry() {
    try {
      const result = await retryProvisioning.mutateAsync();
      setRetryGaps(result.gaps ?? []);
    } catch {
      // Error shown next to the retry action via retryProvisioning.error
    }
  }

  // A retry replaces the create gaps rather than appending to them: a fixed
  // gap should disappear, and a still-open one shouldn't render twice.
  const gaps = retryProvisioning.isSuccess ? retryGaps : createGaps;

  // The theme class rides the page root, so the theme accents the header, the
  // tabs and everything shown on them (Bifrost theming: an ancestor carries
  // the class). Neutral wears the gray theme, not the Portal's default teal.
  const theme = themeClass(platform.theme);

  return (
    <div className={`bf-content ${theme}`}>
      {/* Full-bleed header band: spans the whole page and takes its inline
          padding from Bifrost's page padding, not a centred column. */}
      <header
        style={{
          backgroundColor: "var(--bfc-base-2)",
          borderBottom: "1px solid var(--bfc-base-c-dimmed)",
        }}
      >
        <div className="bf-page-padding-x platform-header-top">
          <div className="bf-page-padding-y platform-header-text">
            <Breadcrumbs>
              <Breadcrumbs.Item>
                <Link to="/">Home</Link>
              </Breadcrumbs.Item>
              <Breadcrumbs.Item>
                {/* Opened from My Hub: the crumb leads back there, not to
                  Platforms. */}
                {fromMyHub ? (
                  <Link to="/myhub">My Hub</Link>
                ) : (
                  <Link to="/platforms">Platforms</Link>
                )}
              </Breadcrumbs.Item>
              <Breadcrumbs.Item>{platform.name}</Breadcrumbs.Item>
            </Breadcrumbs>
            <div
              style={{
                marginTop: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <PlatformLogo logo={platform.logo} size={32} />
              <h1 className="bf-h1" style={{ margin: 0 }}>
                {platform.name}
              </h1>
              <RoleBadge role={platform.userRole} />
            </div>
            {platform.description && (
              <p
                className="bf-p"
                style={{ marginTop: 8, marginBottom: 0, maxWidth: "72ch" }}
              >
                {platform.description}
              </p>
            )}
          </div>
          <PlatformHeaderIllustration theme={platform.theme} />
        </div>
        {/* Button row. Below the `small` breakpoint the labels hide and the
            buttons collapse to their icons (hence the aria-labels). Only Delete
            platform, New client and Set up blueprint are all wired up. */}
        <div
          className="bf-page-padding-x"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            paddingBlock: 12,
            borderTop: "1px solid var(--bfc-base-c-dimmed)",
          }}
        >
          <Button
            state={canDelete ? "alert" : "inactive"}
            onClick={canDelete ? () => setConfirmDelete(true) : undefined}
            aria-label="Delete platform"
          >
            <Icon icon={faTrash} />
            <span className="from-small" style={{ marginLeft: 8 }}>
              Delete platform
            </span>
          </Button>
          <div style={{ display: "flex", gap: 12 }}>
            <Button
              variant="flat"
              aria-label="Set up blueprint"
              state={canEdit && platform.slug ? undefined : "inactive"}
              onClick={
                canEdit && platform.slug ? openBlueprintBuilder : undefined
              }
            >
              <Icon icon={faChartDiagram} />
              <span className="from-small" style={{ marginLeft: 8 }}>
                Set up blueprint
              </span>
            </Button>
            <Button
              aria-label="New client"
              state={canEdit ? undefined : "inactive"}
              onClick={
                canEdit
                  ? () => navigate(`/clients/new?platformId=${platform.id}`)
                  : undefined
              }
            >
              <Icon icon={faPlus} />
              <span className="from-small" style={{ marginLeft: 8 }}>
                New client
              </span>
            </Button>
          </div>
        </div>
      </header>

      {/* Page-level status, shown above the tabs on every tab: the
          provisioning banner with its retry (ADR-0036), create-response gaps
          and setup warnings from the register flow. */}
      {(provisioningStatus !== "active" ||
        gaps.length > 0 ||
        setupWarnings.length > 0) && (
        <div className="bf-page-padding" style={{ display: "grid", gap: 16 }}>
          {deleting && (
            <Message state="warning" header="Delete did not finish">
              <p className="bf-p" style={{ margin: "0 0 12px" }}>
                The delete was interrupted. Every other change to this platform
                is refused until it finishes. Retry to complete it.
              </p>
              {deleteError && (
                <p className="bf-p" style={{ margin: "0 0 12px" }}>
                  {deleteError}
                </p>
              )}
              <Button
                small
                state={deletePlatform.isPending ? "inactive" : undefined}
                onClick={
                  deletePlatform.isPending ? undefined : handleDeletePlatform
                }
              >
                {deletePlatform.isPending ? "Deleting…" : "Finish delete"}
              </Button>
            </Message>
          )}
          {provisioningStatus !== "active" && !deleting && (
            <Message
              state={provisioningStatus === "failed" ? "alert" : "warning"}
              header={
                provisioningStatus === "failed"
                  ? "Provisioning failed"
                  : "Provisioning in progress"
              }
            >
              <p className="bf-p" style={{ margin: "0 0 12px" }}>
                {provisioningStatus === "failed"
                  ? "Setup did not finish. Retry to complete it."
                  : "Setup was interrupted. Retry to complete it."}
              </p>
              {retryProvisioning.error && (
                <p className="bf-p" style={{ margin: "0 0 12px" }}>
                  {retryProvisioning.error instanceof Error
                    ? retryProvisioning.error.message
                    : "Retry failed."}
                </p>
              )}
              <Button
                small
                state={retryProvisioning.isPending ? "inactive" : undefined}
                onClick={retryProvisioning.isPending ? undefined : handleRetry}
              >
                {retryProvisioning.isPending
                  ? "Retrying…"
                  : "Retry provisioning"}
              </Button>
            </Message>
          )}
          {gaps.length > 0 && (
            <Message state="warning" header="Some parts did not apply">
              <p className="bf-p" style={{ margin: "0 0 8px" }}>
                The platform and its clients exist. Grant the items below again
                from the client&apos;s API access page.
              </p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {gaps.map((g) => (
                  <li key={`${g.subject}:${g.message}`}>
                    <strong>{g.subject}:</strong> {g.message}
                  </li>
                ))}
              </ul>
            </Message>
          )}
          {setupWarnings.length > 0 && (
            <Message state="warning" header="Platform created with warnings">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {setupWarnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </Message>
          )}
        </div>
      )}

      <Tabs variant="styled" contentBackground="base">
        <Tabs.Item
          active={tab === "overview"}
          onClick={() => setTab("overview")}
          content={<PlatformOverview platform={platform} canEdit={canEdit} />}
        >
          Overview
        </Tabs.Item>
        <Tabs.Item
          active={tab === "clients"}
          onClick={() => setTab("clients")}
          content={
            <PlatformClientsTab
              platformId={platform.id}
              platformName={platform.name}
              requestedClientId={requestedClient}
              clients={platform.clients}
              themeClass={theme}
            />
          }
        >
          Clients
        </Tabs.Item>
        <Tabs.Item
          active={tab === "blueprint"}
          onClick={() => setTab("blueprint")}
          content={
            platform.slug ? (
              <BlueprintDetail
                slug={platform.slug}
                displayName={platform.name}
              />
            ) : (
              <p className="bf-p" style={{ margin: 0 }}>
                This platform has no slug, so it has no blueprint.
              </p>
            )
          }
        >
          Blueprint
        </Tabs.Item>
        <Tabs.Item
          active={tab === "access"}
          onClick={() => setTab("access")}
          content={<PlatformAccess platform={platform} />}
        >
          Management
        </Tabs.Item>
      </Tabs>

      <Modal
        isOpen={confirmDelete}
        onRequestClose={() => {
          if (deletePlatform.isPending) return;
          setConfirmDelete(false);
          setTypedSlug("");
          deletePlatform.reset();
        }}
        header="Delete platform"
        width={480}
      >
        <Grid gap={16}>
          <p className="bf-p" style={{ margin: 0 }}>
            This permanently deletes <strong>{platform.name}</strong>, its{" "}
            {platform.clients.length} client
            {platform.clients.length !== 1 ? "s" : ""} and its blueprint.
            Credentials are revoked and grants are removed. You cannot undo
            this.
          </p>
          <Input
            label={
              <>
                Type <code>{confirmText}</code> to confirm
              </>
            }
            value={typedSlug}
            onChange={(e) => setTypedSlug(e.target.value)}
            autoComplete="off"
          />
          {deleteError && (
            <Message state="alert" header="Delete failed">
              {deleteError}
            </Message>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <Button
              variant="flat"
              onClick={
                deletePlatform.isPending
                  ? undefined
                  : () => {
                      setConfirmDelete(false);
                      setTypedSlug("");
                    }
              }
              state={deletePlatform.isPending ? "inactive" : undefined}
            >
              Cancel
            </Button>
            <Button
              variant="filled"
              state={
                deletePlatform.isPending || typedSlug !== confirmText
                  ? "inactive"
                  : "alert"
              }
              onClick={
                deletePlatform.isPending || typedSlug !== confirmText
                  ? undefined
                  : handleDeletePlatform
              }
            >
              {deletePlatform.isPending ? "Deleting…" : "Delete platform"}
            </Button>
          </div>
        </Grid>
      </Modal>
    </div>
  );
}
